import { logger } from '@/lib/logger';

/**
 * Sistema de Reset Completo do Site
 * 
 * Limpa todos os dados locais que o site controla:
 * - localStorage (chaves do app)
 * - sessionStorage (chaves do app)
 * - Cookies do domínio
 * - IndexedDB (bancos do app)
 * - Cache Storage (PWA)
 * - Service Workers
 * 
 * NÃO limpa (limitação do navegador):
 * - Histórico de navegação
 * - Dados de outros sites
 * - Cookies HttpOnly do backend
 */

import { supabase } from '@/integrations/supabase/client';

// Prefixos das chaves a serem limpas
const KEY_PREFIXES = [
  'pe_crypto_',
  'drift_',
  'sb-',
  'supabase.',
];

// Prefixos de cookies a serem limpos
const COOKIE_PREFIXES = [
  'drift_',
  'sb-',
];

// Prefixos de IndexedDB a serem limpos
const INDEXEDDB_PREFIXES = [
  'drift',
  'supabase',
];

export interface ResetOptions {
  /** Prefixos de chaves para localStorage/sessionStorage */
  keyPrefixes?: string[];
  /** Prefixos de cookies para limpar */
  cookiePrefixes?: string[];
  /** Recarregar página após reset */
  reload?: boolean;
  /** Callback antes do reload */
  onBeforeReload?: () => void;
}

export interface ResetResult {
  success: boolean;
  localStorageCleared: number;
  sessionStorageCleared: number;
  cookiesCleared: number;
  cacheStorageCleared: number;
  serviceWorkersUnregistered: number;
  indexedDBCleared: number;
  signedOut: boolean;
  errors: string[];
}

/**
 * Função principal de reset completo do site
 */
export async function resetDriftSiteData(options: ResetOptions = {}): Promise<ResetResult> {
  const {
    keyPrefixes = KEY_PREFIXES,
    cookiePrefixes = COOKIE_PREFIXES,
    reload = true,
    onBeforeReload,
  } = options;

  const result: ResetResult = {
    success: true,
    localStorageCleared: 0,
    sessionStorageCleared: 0,
    cookiesCleared: 0,
    cacheStorageCleared: 0,
    serviceWorkersUnregistered: 0,
    indexedDBCleared: 0,
    signedOut: false,
    errors: [],
  };

  // 1) Deslogar via Supabase
  try {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      result.signedOut = true;
    }
  } catch (e) {
    result.errors.push(`SignOut: ${e instanceof Error ? e.message : String(e)}`);
  }

  // 2) LocalStorage: remove só o que é do app
  try {
    const keys = Object.keys(localStorage);
    for (const k of keys) {
      if (keyPrefixes.some(p => k.startsWith(p))) {
        localStorage.removeItem(k);
        result.localStorageCleared++;
      }
    }
  } catch (e) {
    result.errors.push(`LocalStorage: ${e instanceof Error ? e.message : String(e)}`);
  }

  // 3) SessionStorage: remove só o que é do app
  try {
    const keys = Object.keys(sessionStorage);
    for (const k of keys) {
      if (keyPrefixes.some(p => k.startsWith(p))) {
        sessionStorage.removeItem(k);
        result.sessionStorageCleared++;
      }
    }
  } catch (e) {
    result.errors.push(`SessionStorage: ${e instanceof Error ? e.message : String(e)}`);
  }

  // 4) Cookies do domínio (limitado: só cookies sem HttpOnly)
  try {
    const cookies = document.cookie.split(';').map(c => c.trim());
    for (const c of cookies) {
      const [name] = c.split('=');
      if (name && cookiePrefixes.some(p => name.startsWith(p))) {
        // Tentar remover com diferentes combinações de path/domain
        document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
        document.cookie = `${name}=; Max-Age=0; path=/`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        result.cookiesCleared++;
      }
    }
  } catch (e) {
    result.errors.push(`Cookies: ${e instanceof Error ? e.message : String(e)}`);
  }

  // 5) Cache Storage (PWA/requests cache)
  try {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(n => caches.delete(n)));
      result.cacheStorageCleared = cacheNames.length;
    }
  } catch (e) {
    result.errors.push(`CacheStorage: ${e instanceof Error ? e.message : String(e)}`);
  }

  // 6) IndexedDB: apaga bancos do app
  try {
    // @ts-ignore - indexedDB.databases() não existe em todos os browsers
    if (typeof indexedDB !== 'undefined' && indexedDB.databases) {
      // @ts-ignore
      const dbs = await indexedDB.databases();
      const toDelete = dbs.filter((db: { name?: string }) => 
        db?.name && INDEXEDDB_PREFIXES.some(p => db.name!.toLowerCase().startsWith(p))
      );
      
      await Promise.all(
        toDelete.map((db: { name: string }) => new Promise<void>((resolve) => {
          const req = indexedDB.deleteDatabase(db.name);
          req.onsuccess = () => {
            result.indexedDBCleared++;
            resolve();
          };
          req.onerror = () => resolve();
          req.onblocked = () => resolve();
        }))
      );
    }
  } catch (e) {
    result.errors.push(`IndexedDB: ${e instanceof Error ? e.message : String(e)}`);
  }

  // 7) Service Worker: unregister para evitar "versão antiga"
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));
      result.serviceWorkersUnregistered = regs.length;
    }
  } catch (e) {
    result.errors.push(`ServiceWorker: ${e instanceof Error ? e.message : String(e)}`);
  }

  // 8) Callback antes do reload
  if (onBeforeReload) {
    try {
      onBeforeReload();
    } catch (e) {
      result.errors.push(`onBeforeReload: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  // 9) Recarregar página em estado limpo
  if (reload) {
    // Usa replace para não adicionar ao histórico
    window.location.replace(window.location.pathname + '?reset=1');
  }

  result.success = result.errors.length === 0;
  return result;
}

/**
 * Versão síncrona simplificada para uso em Error Boundaries
 * (não aguarda operações async, apenas dispara e recarrega)
 */
export function resetDriftSiteDataSync(): void {
  // 1) SignOut (fire and forget)
  supabase.auth.signOut().catch(() => {});

  // 2) LocalStorage
  try {
    const keys = Object.keys(localStorage);
    for (const k of keys) {
      if (KEY_PREFIXES.some(p => k.startsWith(p))) {
        localStorage.removeItem(k);
      }
    }
  } catch {}

  // 3) SessionStorage
  try {
    const keys = Object.keys(sessionStorage);
    for (const k of keys) {
      if (KEY_PREFIXES.some(p => k.startsWith(p))) {
        sessionStorage.removeItem(k);
      }
    }
  } catch {}

  // 4) Cookies
  try {
    const cookies = document.cookie.split(';').map(c => c.trim());
    for (const c of cookies) {
      const [name] = c.split('=');
      if (name && COOKIE_PREFIXES.some(p => name.startsWith(p))) {
        document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
      }
    }
  } catch {}

  // 5) Cache Storage (fire and forget)
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(n => caches.delete(n).catch(() => {}));
    }).catch(() => {});
  }

  // 6) Service Worker (fire and forget)
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(regs => {
      regs.forEach(r => r.unregister().catch(() => {}));
    }).catch(() => {});
  }

  // 7) Recarrega
  window.location.replace(window.location.pathname + '?reset=1');
}

/**
 * Verifica e reseta automaticamente quando a versão do storage muda
 * Deve ser chamado no bootstrap do app
 */
const STORAGE_VERSION = '8'; // v8: Força limpeza de cache antigo (Performance/Notifications fix)
const STORAGE_VERSION_KEY = 'drift_storage_version';

export function ensureStorageVersion(): boolean {
  try {
    const v = localStorage.getItem(STORAGE_VERSION_KEY);
    if (v !== STORAGE_VERSION) {
      logger.debug(`Storage version changed from ${v} to ${STORAGE_VERSION}, cleaning old data`, { component: 'DriftReset' });
      
      // Limpa apenas drift_* e pe_crypto_*
      const keys = Object.keys(localStorage);
      let cleared = 0;
      for (const k of keys) {
        if (k.startsWith('drift_') || k.startsWith('pe_crypto_')) {
          localStorage.removeItem(k);
          cleared++;
        }
      }
      
      localStorage.setItem(STORAGE_VERSION_KEY, STORAGE_VERSION);
      logger.debug(`Cleaned ${cleared} keys, new version: ${STORAGE_VERSION}`, { component: 'DriftReset' });
      return true; // Indica que houve limpeza
    }
    return false;
  } catch (e) {
    logger.warn('Error checking storage version', { component: 'DriftReset' });
    return false;
  }
}

/**
 * Retorna estatísticas dos dados armazenados
 */
export function getStorageStats(): {
  localStorageKeys: number;
  localStorageSize: string;
  sessionStorageKeys: number;
  cookies: number;
} {
  let localStorageKeys = 0;
  let localStorageBytes = 0;
  let sessionStorageKeys = 0;
  let cookies = 0;

  try {
    const keys = Object.keys(localStorage);
    for (const k of keys) {
      if (KEY_PREFIXES.some(p => k.startsWith(p))) {
        localStorageKeys++;
        localStorageBytes += (localStorage.getItem(k) || '').length * 2; // UTF-16
      }
    }
  } catch {}

  try {
    const keys = Object.keys(sessionStorage);
    for (const k of keys) {
      if (KEY_PREFIXES.some(p => k.startsWith(p))) {
        sessionStorageKeys++;
      }
    }
  } catch {}

  try {
    const cookieList = document.cookie.split(';').map(c => c.trim());
    for (const c of cookieList) {
      const [name] = c.split('=');
      if (name && COOKIE_PREFIXES.some(p => name.startsWith(p))) {
        cookies++;
      }
    }
  } catch {}

  // Formatar tamanho
  const formatSize = (bytes: number) => {
    if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes} B`;
  };

  return {
    localStorageKeys,
    localStorageSize: formatSize(localStorageBytes),
    sessionStorageKeys,
    cookies,
  };
}
